import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { loadAllGoogleFonts } from '../../utils/googleFonts';

export default function RichTextEditorPreview() {
  const [content, setContent] = useState('');
  const [projectMeta, setProjectMeta] = useState(null);
  const contentRef = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOrientation, setImageOrientation] = useState('landscape');
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const thumbnailContainerRef = useRef(null);

  // Load all Google Fonts
  useEffect(() => {
    loadAllGoogleFonts();
  }, []);

  // Configure DOMPurify to allow all CSS properties
  useEffect(() => {
    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
      // Allow all style attributes
      if (data.attrName === 'style') {
        // Don't filter any CSS properties
        return;
      }
    });

    return () => {
      DOMPurify.removeAllHooks();
    };
  }, []);

  // Re-add interactive functionality after content is rendered
  useEffect(() => {
    if (contentRef.current && content) {
      // Re-enable embed video interaction
      const embeds = contentRef.current.querySelectorAll('.editor-embed-wrapper');
      embeds.forEach(embed => {
        const overlay = embed.querySelector('.embed-overlay');
        if (overlay) {
          // Remove the overlay in preview mode to allow video interaction
          overlay.remove();
        }
        
        // Re-enable pointer events on iframe and content
        const iframe = embed.querySelector('iframe');
        if (iframe) {
          iframe.style.pointerEvents = 'auto';
          iframe.removeAttribute('tabindex');
        }
        
        // Re-enable pointer events on embed content
        const embedContent = embed.querySelector('.embed-content');
        if (embedContent) {
          embedContent.style.pointerEvents = 'auto';
          // Re-enable pointer events on all children
          embedContent.querySelectorAll('*').forEach(el => {
            el.style.pointerEvents = 'auto';
          });
        }
      });

      // Re-add copy functionality to codeblocks
      const copyButtons = contentRef.current.querySelectorAll('.editor-code-header button');
      copyButtons.forEach(button => {
        const codeBlock = button.closest('.editor-code-block-wrapper');
        if (codeBlock) {
          const codeContent = codeBlock.getAttribute('data-code');
          if (codeContent) {
            button.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              navigator.clipboard.writeText(codeContent).then(() => {
                const originalHTML = button.innerHTML;
                button.innerHTML = 'Copied!';
                button.style.fontSize = '11px';
                setTimeout(() => {
                  button.innerHTML = originalHTML;
                  button.style.fontSize = '';
                }, 2000);
              }).catch(err => console.error('Failed to copy:', err));
            };
          }
        }
      });

      // Re-add expand functionality to wrapped codeblocks
      const expandButtons = contentRef.current.querySelectorAll('.editor-code-block-wrapper button[title*="show full code"]');
      expandButtons.forEach(button => {
        const wrapper = button.closest('.editor-code-block-wrapper');
        if (wrapper) {
          const shade = wrapper.querySelector('div[style*="linear-gradient"]');
          const expandBtnContainer = button.parentElement;
          
          button.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            wrapper.style.maxHeight = wrapper.style.maxHeight === '360px' ? 'none' : '360px';
            if (shade) shade.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
            if (expandBtnContainer) expandBtnContainer.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
          };
        }
      });

      // Re-add tooltip functionality
      const tooltipWrappers = contentRef.current.querySelectorAll('.editor-tooltip-wrapper');
      tooltipWrappers.forEach(wrapper => {
        const tooltipText = wrapper.getAttribute('data-tooltip');
        if (tooltipText) {
          // Remove any existing tooltip elements
          const existingTooltip = wrapper.querySelector('.editor-tooltip');
          if (existingTooltip) existingTooltip.remove();
          
          // Create new tooltip element
          const tooltip = document.createElement('span');
          tooltip.className = 'editor-tooltip';
          tooltip.textContent = tooltipText;
          tooltip.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            padding: 0.5em 0.8em;
            opacity: 0;
            pointer-events: none;
            transition: all 0.3s;
            color: #fff;
            background: #105652;
            border-radius: 0.375em;
            font-size: 14px;
            z-index: 1000;
            visibility: hidden;
            max-width: 280px;
            width: max-content;
            white-space: normal;
            word-wrap: break-word;
            text-align: center;
            line-height: 1.4;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          `;
          
          // Create tooltip arrow
          const arrow = document.createElement('span');
          arrow.className = 'editor-tooltip-arrow';
          arrow.style.cssText = `
            position: absolute;
            content: "";
            height: 0.6em;
            width: 0.6em;
            bottom: -0.2em;
            left: 50%;
            transform: translate(-50%, 0) rotate(45deg);
            background: #105652;
          `;
          tooltip.appendChild(arrow);
          
          // Position tooltip function
          const positionTooltip = () => {
            const wrapperRect = wrapper.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            // Calculate ideal position (centered above the text)
            let left = wrapperRect.left + (wrapperRect.width / 2) - (tooltipRect.width / 2);
            let top = wrapperRect.top - tooltipRect.height - 10;
            
            // Adjust horizontal position to prevent overflow
            const padding = 10;
            if (left < padding) {
              left = padding;
            } else if (left + tooltipRect.width > viewportWidth - padding) {
              left = viewportWidth - tooltipRect.width - padding;
            }
            
            // Adjust vertical position if tooltip would go above viewport
            if (top < padding) {
              top = wrapperRect.bottom + 10;
              // Move arrow to top for bottom positioning
              arrow.style.top = '-0.2em';
              arrow.style.bottom = 'auto';
              arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
            } else {
              // Reset arrow to bottom for top positioning
              arrow.style.top = 'auto';
              arrow.style.bottom = '-0.2em';
              arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
            }
            
            // Calculate arrow position relative to tooltip
            const arrowLeft = ((wrapperRect.left + wrapperRect.width / 2) - left) / tooltipRect.width * 100;
            arrow.style.left = Math.max(10, Math.min(90, arrowLeft)) + '%';
            
            tooltip.style.left = left + 'px';
            tooltip.style.top = top + 'px';
          };
          
          // Add hover functionality with positioning
          wrapper.onmouseenter = () => {
            // Temporarily show tooltip to measure it
            tooltip.style.visibility = 'visible';
            tooltip.style.opacity = '0';
            document.body.appendChild(tooltip);
            
            // Position the tooltip
            positionTooltip();
            
            // Show tooltip with animation
            tooltip.style.opacity = '1';
            tooltip.style.pointerEvents = 'auto';
          };
          
          wrapper.onmouseleave = () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
            tooltip.style.pointerEvents = 'none';
            // Remove from body after animation
            setTimeout(() => {
              if (tooltip.parentNode === document.body) {
                document.body.removeChild(tooltip);
              }
            }, 300);
          };
          
          // Note: Click editing is only available in editor mode, not preview
          wrapper.style.cursor = 'help'; // Change cursor to indicate tooltip in preview
          
          // Store tooltip element reference on wrapper for cleanup
          wrapper._tooltipElement = tooltip;
        }
      });

      // Re-add quote block styling
      const quoteWrappers = contentRef.current.querySelectorAll('.editor-quote-wrapper');
      quoteWrappers.forEach(wrapper => {
        const theme = wrapper.getAttribute('data-theme') || 'modern';
        
        // Apply theme styles based on theme ID
        const getQuoteThemeStyles = (themeId) => {
          const themes = {
            modern: {
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '16px 20px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#495057',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap'
            },
            classic: {
              backgroundColor: '#fffbf0',
              border: '1px solid #20b2aa',
              borderRadius: '8px',
              padding: '16px 20px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#8b4513',
              fontStyle: 'italic',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap'
            },
            vibrant: {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap'
            },
            minimal: {
              backgroundColor: 'transparent',
              border: 'none',
              borderLeft: '3px solid #dee2e6',
              padding: '12px 0 12px 16px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#495057',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap'
            },
            'code-like': {
              backgroundColor: '#2d3748',
              color: '#4fd1c7',
              borderRadius: '8px',
              padding: '16px 20px',
              margin: '16px auto',
              fontSize: '13px',
              lineHeight: '1.4',
              fontFamily: 'Monaco, Consolas, monospace',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap'
            },
            radio: {
              backgroundColor: '#1a1a1a',
              color: '#00ff41',
              border: '2px solid #00ff41',
              borderRadius: '15px',
              padding: '18px 24px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.6',
              fontFamily: 'Courier New, monospace',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap',
              boxShadow: '0 0 20px rgba(0, 255, 65, 0.3), inset 0 0 20px rgba(0, 255, 65, 0.1)'
            },
            vintage: {
              backgroundColor: '#f4f1e8',
              color: '#5d4e37',
              border: '3px double #8b7355',
              borderRadius: '0',
              padding: '20px 24px',
              margin: '16px auto',
              fontSize: '15px',
              lineHeight: '1.7',
              fontFamily: 'Georgia, serif',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap',
              boxShadow: 'inset 0 0 0 1px #d4c4a8, 0 2px 8px rgba(139, 115, 85, 0.2)'
            },
            neon: {
              backgroundColor: '#0a0a0a',
              color: '#ff006e',
              border: '1px solid #ff006e',
              borderRadius: '20px',
              padding: '18px 22px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'Arial, sans-serif',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap',
              boxShadow: '0 0 30px rgba(255, 0, 110, 0.5), inset 0 0 30px rgba(255, 0, 110, 0.1)',
              textShadow: '0 0 10px rgba(255, 0, 110, 0.8)'
            },
            scifi: {
              background: 'linear-gradient(135deg, #0c1445 0%, #1e3c72 50%, #2a5298 100%)',
              color: '#00d4ff',
              border: '1px solid #00d4ff',
              borderRadius: '0 15px 0 15px',
              padding: '16px 20px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'Orbitron, monospace',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap',
              boxShadow: '0 0 25px rgba(0, 212, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            },
            paper: {
              backgroundColor: '#fefefe',
              color: '#2c3e50',
              border: '1px solid #bdc3c7',
              borderRadius: '3px',
              padding: '18px 22px',
              margin: '16px auto',
              fontSize: '14px',
              lineHeight: '1.6',
              fontFamily: 'Times New Roman, serif',
              maxWidth: '70%',
              width: '70%',
              whiteSpace: 'pre-wrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
            }
          };
          return themes[themeId] || themes.modern;
        };
        
        const themeStyles = getQuoteThemeStyles(theme);
        
        // Clear all existing background properties first
        wrapper.style.background = '';
        wrapper.style.backgroundColor = '';
        wrapper.style.border = '';
        wrapper.style.borderLeft = '';
        wrapper.style.borderRadius = '';
        wrapper.style.padding = '';
        wrapper.style.margin = '';
        wrapper.style.fontSize = '';
        wrapper.style.lineHeight = '';
        wrapper.style.color = '';
        wrapper.style.fontStyle = '';
        wrapper.style.fontFamily = '';
        wrapper.style.maxWidth = '';
        wrapper.style.width = '';
        wrapper.style.whiteSpace = '';
        wrapper.style.boxShadow = '';
        wrapper.style.textShadow = '';
        wrapper.style.position = '';
        wrapper.style.textTransform = '';
        wrapper.style.letterSpacing = '';
        
        // Apply new theme styles
        Object.assign(wrapper.style, themeStyles);
        
        // Apply white-space: pre-wrap to quote text element
        const quoteTextElement = wrapper.querySelector('.quote-text');
        if (quoteTextElement) {
          quoteTextElement.style.whiteSpace = 'pre-wrap';
        }
      });

      // Re-add FAQ functionality
      const faqWrappers = contentRef.current.querySelectorAll('.editor-faq-wrapper');
      faqWrappers.forEach(wrapper => {
        const questionElement = wrapper.querySelector('.faq-question');
        const answerElement = wrapper.querySelector('.faq-answer');
        const chevronIcon = wrapper.querySelector('.faq-chevron');
        const theme = wrapper.getAttribute('data-theme') || 'minimal';
        
        // Apply theme styles
        const getFaqThemeStyles = (themeId) => {
          const themes = {
            minimal: {
              containerStyle: {
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                margin: '1rem 0',
                backgroundColor: 'white',
                overflow: 'hidden'
              },
              questionStyle: {
                padding: '16px 20px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s'
              },
              answerStyle: {
                padding: '0 20px 16px 20px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#6b7280',
                borderTop: '1px solid #f3f4f6'
              },
              iconStyle: {
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#105652',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                transition: 'transform 0.2s'
              }
            },
            modern: {
              containerStyle: {
                border: '2px solid #105652',
                borderRadius: '12px',
                margin: '1rem 0',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(16, 86, 82, 0.1)',
                overflow: 'hidden'
              },
              questionStyle: {
                padding: '18px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#105652',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)'
              },
              answerStyle: {
                padding: '0 24px 20px 24px',
                fontSize: '14px',
                lineHeight: '1.7',
                color: '#4b5563',
                backgroundColor: '#fafafa'
              },
              iconStyle: {
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                backgroundColor: '#105652',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                boxShadow: '0 2px 4px rgba(16, 86, 82, 0.2)'
              }
            },
            card: {
              containerStyle: {
                backgroundColor: 'white',
                borderRadius: '12px',
                margin: '1rem 0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: '1px solid #f1f5f9',
                overflow: 'hidden'
              },
              questionStyle: {
                padding: '20px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1f2937',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
              },
              answerStyle: {
                padding: '0 24px 24px 24px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#6b7280'
              },
              iconStyle: {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#4C1D95',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                boxShadow: '0 2px 8px rgba(76, 29, 149, 0.3)'
              }
            },
            gradient: {
              containerStyle: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                margin: '1rem 0',
                padding: '2px',
                overflow: 'hidden'
              },
              questionStyle: {
                padding: '18px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s',
                backgroundColor: 'transparent',
                borderRadius: '14px'
              },
              answerStyle: {
                padding: '0 24px 20px 24px',
                fontSize: '14px',
                lineHeight: '1.7',
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                margin: '0 2px 2px 2px',
                borderRadius: '0 0 14px 14px'
              },
              iconStyle: {
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }
            },
            outlined: {
              containerStyle: {
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                margin: '1rem 0',
                backgroundColor: 'transparent',
                overflow: 'hidden'
              },
              questionStyle: {
                padding: '16px 20px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s',
                borderLeft: '4px solid #105652'
              },
              answerStyle: {
                padding: '0 20px 16px 24px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#6b7280',
                borderTop: '1px solid #f3f4f6',
                backgroundColor: '#fafafa'
              },
              iconStyle: {
                width: '22px',
                height: '22px',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: '#105652',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                transition: 'all 0.2s',
                border: '2px solid #105652'
              }
            }
          };
          return themes[themeId] || themes.minimal;
        };
        
        const themeStyles = getFaqThemeStyles(theme);
        
        // Apply container styles - clear first
        wrapper.style.cssText = '';
        wrapper.removeAttribute('style');
        Object.assign(wrapper.style, themeStyles.containerStyle);
        
        if (questionElement && answerElement) {
          // Clear existing styles completely
          questionElement.style.cssText = '';
          questionElement.removeAttribute('style');
          answerElement.style.cssText = '';
          answerElement.removeAttribute('style');
          
          // Apply question styles
          Object.assign(questionElement.style, themeStyles.questionStyle);
          
          // Apply answer styles
          Object.assign(answerElement.style, themeStyles.answerStyle);
          
          // Start collapsed
          answerElement.style.display = 'none';
          
          // Handle chevron icon - remove existing and create new
          if (chevronIcon) {
            chevronIcon.remove();
          }
          
          // Create new chevron with proper styling
          const newChevronIcon = document.createElement('div');
          newChevronIcon.className = 'faq-chevron';
          newChevronIcon.innerHTML = '▼';
          Object.assign(newChevronIcon.style, themeStyles.iconStyle);
          questionElement.appendChild(newChevronIcon);
          
          // Add click functionality
          questionElement.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = answerElement.style.display !== 'none';
            answerElement.style.display = isOpen ? 'none' : 'block';
            if (newChevronIcon) {
              newChevronIcon.innerHTML = isOpen ? '▼' : '▲';
              newChevronIcon.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
            }
          };
          
          // Add hover effects
          questionElement.onmouseenter = () => {
            if (themeStyles.questionHoverStyle) {
              Object.assign(questionElement.style, themeStyles.questionHoverStyle);
            }
          };
          
          questionElement.onmouseleave = () => {
            Object.assign(questionElement.style, themeStyles.questionStyle);
          };
        }
      });

      // Re-add button hover effects
      const buttonWrappers = contentRef.current.querySelectorAll('.editor-button-wrapper');
      buttonWrappers.forEach(wrapper => {
        const buttonLink = wrapper.querySelector('a');
        const theme = wrapper.getAttribute('data-theme') || 'primary';
        const alignment = wrapper.getAttribute('data-alignment') || 'center';
        
        // Apply alignment to wrapper
        const getAlignmentStyle = (alignment) => {
          switch (alignment) {
            case 'left': return 'text-align: left;';
            case 'right': return 'text-align: right;';
            case 'center':
            default: return 'text-align: center;';
          }
        };
        
        wrapper.style.cssText = `${getAlignmentStyle(alignment)} margin: 1rem 0;`;
        
        if (buttonLink) {
          // Get theme styles (simplified for preview)
          const getThemeStyles = (themeId) => {
            const themes = {
              primary: {
                style: { backgroundColor: '#105652', color: 'white' },
                hoverStyle: { backgroundColor: '#0d4a47', transform: 'translateY(-2px)' }
              },
              outline: {
                style: { backgroundColor: 'transparent', color: '#105652', border: '2px solid #105652' },
                hoverStyle: { backgroundColor: '#105652', color: 'white' }
              },
              gradient: {
                style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' },
                hoverStyle: { transform: 'translateY(-3px)' }
              },
              minimal: {
                style: { backgroundColor: 'transparent', color: '#333', border: '1px solid #ddd' },
                hoverStyle: { backgroundColor: '#f8f9fa' }
              },
              rounded: {
                style: { backgroundColor: '#1E8479', color: 'white' },
                hoverStyle: { backgroundColor: '#196b61', transform: 'scale(1.05)' }
              }
            };
            return themes[themeId] || themes.primary;
          };
          
          const themeStyles = getThemeStyles(theme);
          
          buttonLink.addEventListener('mouseenter', () => {
            Object.assign(buttonLink.style, themeStyles.hoverStyle);
          });
          
          buttonLink.addEventListener('mouseleave', () => {
            Object.assign(buttonLink.style, themeStyles.style);
          });
        }
      });
      buttonWrappers.forEach(wrapper => {
        const buttonLink = wrapper.querySelector('a');
        const theme = wrapper.getAttribute('data-theme') || 'primary';
        const alignment = wrapper.getAttribute('data-alignment') || 'center';
        
        // Apply alignment to wrapper
        const getAlignmentStyle = (alignment) => {
          switch (alignment) {
            case 'left': return 'text-align: left;';
            case 'right': return 'text-align: right;';
            case 'center':
            default: return 'text-align: center;';
          }
        };
        
        wrapper.style.cssText = `${getAlignmentStyle(alignment)} margin: 1rem 0;`;
        
        if (buttonLink) {
          // Get theme styles (simplified for preview)
          const getThemeStyles = (themeId) => {
            const themes = {
              primary: {
                style: { backgroundColor: '#105652', color: 'white' },
                hoverStyle: { backgroundColor: '#0d4a47', transform: 'translateY(-2px)' }
              },
              outline: {
                style: { backgroundColor: 'transparent', color: '#105652', border: '2px solid #105652' },
                hoverStyle: { backgroundColor: '#105652', color: 'white' }
              },
              gradient: {
                style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' },
                hoverStyle: { transform: 'translateY(-3px)' }
              },
              minimal: {
                style: { backgroundColor: 'transparent', color: '#333', border: '1px solid #ddd' },
                hoverStyle: { backgroundColor: '#f8f9fa' }
              },
              rounded: {
                style: { backgroundColor: '#1E8479', color: 'white' },
                hoverStyle: { backgroundColor: '#196b61', transform: 'scale(1.05)' }
              }
            };
            return themes[themeId] || themes.primary;
          };
          
          const themeStyles = getThemeStyles(theme);
          
          buttonLink.addEventListener('mouseenter', () => {
            Object.assign(buttonLink.style, themeStyles.hoverStyle);
          });
          
          buttonLink.addEventListener('mouseleave', () => {
            Object.assign(buttonLink.style, themeStyles.style);
          });
        }
      });

      // Re-add file download functionality
      const fileDownloadWrappers = contentRef.current.querySelectorAll('.editor-file-download-wrapper');
      fileDownloadWrappers.forEach(wrapper => {
        const filesData = wrapper.getAttribute('data-files');
        
        if (filesData) {
          try {
            const files = JSON.parse(filesData);
            const downloadBtns = wrapper.querySelectorAll('.download-btn');
            
            downloadBtns.forEach(downloadBtn => {
              // Remove any existing event listeners
              downloadBtn.onclick = null;
              
              // Add new download functionality for individual files
              downloadBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                const fileUrl = downloadBtn.getAttribute('data-file-url');
                const fileName = downloadBtn.getAttribute('data-file-name');
                
                if (fileUrl && fileName) {
                  console.log(`Downloading ${fileName}...`);
                  const link = document.createElement('a');
                  link.href = fileUrl;
                  link.download = fileName;
                  link.target = '_blank';
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              });
              
              // Add hover effects
              downloadBtn.addEventListener('mouseenter', () => {
                downloadBtn.style.textDecoration = 'underline';
              });
              
              downloadBtn.addEventListener('mouseleave', () => {
                downloadBtn.style.textDecoration = 'none';
              });
            });
            
            // Add hover effects to the card
            const card = wrapper.querySelector('.file-download-card');
            if (card) {
              card.addEventListener('mouseenter', () => {
                card.style.backgroundColor = '#d3ddf1';
                card.style.border = '1.5px solid #1677ff';
              });
              
              card.addEventListener('mouseleave', () => {
                card.style.backgroundColor = '#f2f3f7';
                card.style.border = '1.5px solid #f2f3f7';
              });
            }
          } catch (error) {
            console.error('Error parsing file download data:', error);
          }
        }
      });

      // Re-add image functionality
      const imageWrappers = contentRef.current.querySelectorAll('.editor-image-wrapper');
      imageWrappers.forEach(wrapper => {
        // Images in preview mode are just displayed, no special functionality needed
        // The alignment is already handled by the wrapper's parent container
        const img = wrapper.querySelector('img');
        if (img) {
          // Ensure images are responsive in preview
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
        }
      });

      // Re-add audio functionality
      const audioWrappers = contentRef.current.querySelectorAll('.editor-audio-wrapper');
      audioWrappers.forEach(wrapper => {
        // Re-initialize audio player functionality for preview
        const audio = wrapper.querySelector('audio');
        const playPauseBtn = wrapper.querySelector('.play-pause-btn');
        const playPauseIcon = wrapper.querySelector('.play-pause-icon');
        const currentTimeSpan = wrapper.querySelector('.current-time');
        const totalTimeSpan = wrapper.querySelector('.total-time');
        const progressSlider = wrapper.querySelector('.slider');
        const progress = wrapper.querySelector('.progress');
        const volumeBtn = wrapper.querySelector('.volume-btn');
        const volumeControls = wrapper.querySelector('.volume-controls');
        
        if (audio && playPauseBtn) {
          let isPlaying = false;
          let isDragging = false;
          
          // Format time helper
          const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };
          
          // Load audio metadata
          audio.addEventListener('loadedmetadata', () => {
            if (totalTimeSpan) {
              totalTimeSpan.textContent = formatTime(audio.duration);
            }
          });
          
          // Update progress
          audio.addEventListener('timeupdate', () => {
            if (!isDragging && progress && currentTimeSpan) {
              const progressPercent = (audio.currentTime / audio.duration) * 100;
              progress.style.width = `${progressPercent}%`;
              currentTimeSpan.textContent = formatTime(audio.currentTime);
            }
          });
          
          // Play/Pause functionality
          playPauseBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (isPlaying) {
              audio.pause();
              if (playPauseIcon) {
                playPauseIcon.setAttribute('d', 'M18 12L0 24V0'); // Play icon
              }
              isPlaying = false;
            } else {
              audio.play();
              if (playPauseIcon) {
                playPauseIcon.setAttribute('d', 'M0 0h6v24H0zM12 0h6v24h-6z'); // Pause icon
              }
              isPlaying = true;
            }
          });
          
          // Progress slider functionality
          if (progressSlider) {
            progressSlider.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              const rect = progressSlider.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const progressPercent = (clickX / rect.width) * 100;
              const newTime = (progressPercent / 100) * audio.duration;
              
              audio.currentTime = newTime;
              if (progress) {
                progress.style.width = `${progressPercent}%`;
              }
            });
          }
          
          // Volume controls
          if (volumeBtn && volumeControls) {
            volumeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              volumeControls.classList.toggle('hidden');
            });
            
            // Hide volume controls when clicking elsewhere
            document.addEventListener('click', (e) => {
              if (!e.target.closest('.volume')) {
                volumeControls.classList.add('hidden');
              }
            });
          }
        }
      });

      // Re-add video functionality
      const videoWrappers = contentRef.current.querySelectorAll('.editor-video-wrapper');
      videoWrappers.forEach(wrapper => {
        // Re-initialize video player functionality for preview
        const video = wrapper.querySelector('.video-element');
        const playPauseBtn = wrapper.querySelector('.play-pause-btn');
        const playIcon = wrapper.querySelector('.play-icon');
        const pauseIcon = wrapper.querySelector('.pause-icon');
        const playOverlay = wrapper.querySelector('.play-overlay');
        const timeDisplay = wrapper.querySelector('.time-display');
        const progressContainer = wrapper.querySelector('.progress-container');
        const progressBar = wrapper.querySelector('.progress-bar');
        const progressThumb = wrapper.querySelector('.progress-thumb');
        const speedBtn = wrapper.querySelector('.speed-btn');
        const speedMenu = wrapper.querySelector('.speed-menu');
        const volumeBtn = wrapper.querySelector('.volume-btn');
        const volumeSlider = wrapper.querySelector('.volume-slider');
        const volumeProgress = wrapper.querySelector('.volume-progress');
        const volumeThumb = wrapper.querySelector('.volume-thumb');
        const fullscreenBtn = wrapper.querySelector('.fullscreen-btn');
        const controls = wrapper.querySelector('.video-controls');
        const loadingSpinner = wrapper.querySelector('.loading-spinner');
        
        if (video && playPauseBtn) {
          let isPlaying = false;
          let isDragging = false;
          let currentSpeed = 1;
          let hideControlsTimeout;
          
          // Format time helper
          const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };
          
          // Show/hide controls
          const showControls = () => {
            if (controls) {
              controls.style.opacity = '1';
              controls.style.pointerEvents = 'auto';
              clearTimeout(hideControlsTimeout);
              hideControlsTimeout = setTimeout(() => {
                if (isPlaying) {
                  controls.style.opacity = '0';
                  controls.style.pointerEvents = 'none';
                }
              }, 3000);
            }
          };
          
          // Video event listeners
          video.addEventListener('loadedmetadata', () => {
            if (timeDisplay) {
              timeDisplay.textContent = `0:00 / ${formatTime(video.duration)}`;
            }
            if (loadingSpinner) {
              loadingSpinner.style.display = 'none';
            }
          });
          
          video.addEventListener('timeupdate', () => {
            if (!isDragging && progressBar && timeDisplay) {
              const progress = (video.currentTime / video.duration) * 100;
              progressBar.style.width = `${progress}%`;
              timeDisplay.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
            }
          });
          
          video.addEventListener('waiting', () => {
            if (loadingSpinner) {
              loadingSpinner.style.display = 'block';
            }
          });
          
          video.addEventListener('canplay', () => {
            if (loadingSpinner) {
              loadingSpinner.style.display = 'none';
            }
          });
          
          // Play/Pause functionality
          const togglePlayPause = () => {
            if (isPlaying) {
              video.pause();
              if (playIcon) playIcon.style.display = 'block';
              if (pauseIcon) pauseIcon.style.display = 'none';
              if (playOverlay) playOverlay.style.display = 'flex';
              isPlaying = false;
            } else {
              video.play();
              if (playIcon) playIcon.style.display = 'none';
              if (pauseIcon) pauseIcon.style.display = 'block';
              if (playOverlay) playOverlay.style.display = 'none';
              isPlaying = true;
              showControls();
            }
          };
          
          playPauseBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            togglePlayPause();
          });
          
          if (playOverlay) {
            playOverlay.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePlayPause();
            });
          }
          
          // Progress bar functionality
          if (progressContainer) {
            progressContainer.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = progressContainer.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const progress = (clickX / rect.width) * 100;
              const newTime = (progress / 100) * video.duration;
              video.currentTime = newTime;
              if (progressBar) {
                progressBar.style.width = `${progress}%`;
              }
            });
          }
          
          // Speed control
          if (speedBtn && speedMenu) {
            speedBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              speedMenu.style.display = speedMenu.style.display === 'flex' ? 'none' : 'flex';
            });
            
            wrapper.querySelectorAll('.speed-option').forEach(option => {
              option.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const speed = parseFloat(option.dataset.speed);
                video.playbackRate = speed;
                speedBtn.textContent = `${speed}x`;
                speedMenu.style.display = 'none';
                currentSpeed = speed;
              });
            });
          }
          
          // Volume control
          if (volumeSlider) {
            volumeSlider.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = volumeSlider.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const volume = clickX / rect.width;
              video.volume = Math.max(0, Math.min(1, volume));
              if (volumeProgress) {
                volumeProgress.style.width = `${volume * 100}%`;
              }
            });
          }
          
          if (volumeBtn) {
            volumeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              video.muted = !video.muted;
              if (volumeProgress) {
                volumeProgress.style.width = video.muted ? '0%' : `${video.volume * 100}%`;
              }
            });
          }
          
          // Fullscreen functionality
          if (fullscreenBtn) {
            fullscreenBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (video.requestFullscreen) {
                video.requestFullscreen();
              } else if (video.webkitRequestFullscreen) {
                video.webkitRequestFullscreen();
              } else if (video.msRequestFullscreen) {
                video.msRequestFullscreen();
              }
            });
          }
          
          // Mouse events for controls
          wrapper.addEventListener('mouseenter', showControls);
          wrapper.addEventListener('mousemove', showControls);
          wrapper.addEventListener('mouseleave', () => {
            if (isPlaying && controls) {
              controls.style.opacity = '0';
              controls.style.pointerEvents = 'none';
            }
          });
          
          // Progress thumb hover
          if (progressContainer && progressThumb) {
            progressContainer.addEventListener('mouseenter', () => {
              progressThumb.style.opacity = '1';
            });
            progressContainer.addEventListener('mouseleave', () => {
              progressThumb.style.opacity = '0';
            });
          }
          
          // Volume thumb hover
          if (volumeSlider && volumeThumb) {
            volumeSlider.addEventListener('mouseenter', () => {
              volumeThumb.style.opacity = '1';
            });
            volumeSlider.addEventListener('mouseleave', () => {
              volumeThumb.style.opacity = '0';
            });
          }
          
          // Hide speed menu when clicking elsewhere
          document.addEventListener('click', (e) => {
            if (!e.target.closest('.speed-control') && speedMenu) {
              speedMenu.style.display = 'none';
            }
          });
        }
      });
    }
  }, [content]);

  useEffect(() => {
    // Load content from localStorage
    const savedContent = localStorage.getItem('richTextEditorContent');
    if (savedContent) {
      setContent(savedContent);
    }
    
    // Load project metadata from localStorage
    const savedProjectMeta = localStorage.getItem('richTextEditorProjectMeta');
    if (savedProjectMeta) {
      try {
        const parsedMeta = JSON.parse(savedProjectMeta);
        console.log('📸 Preview - Loaded project metadata:', parsedMeta);
        console.log('📸 Preview - Gallery URLs:', parsedMeta.galleryUrls);
        setProjectMeta(parsedMeta);
      } catch (error) {
        console.error('Error parsing project metadata:', error);
      }
    }

    // Listen for storage changes (when editor saves)
    const handleStorageChange = () => {
      const updatedContent = localStorage.getItem('richTextEditorContent');
      if (updatedContent) {
        setContent(updatedContent);
      }
      
      const updatedProjectMeta = localStorage.getItem('richTextEditorProjectMeta');
      if (updatedProjectMeta) {
        try {
          setProjectMeta(JSON.parse(updatedProjectMeta));
        } catch (error) {
          console.error('Error parsing project metadata:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Poll for changes every 500ms (for same-window updates)
    const interval = setInterval(() => {
      const updatedContent = localStorage.getItem('richTextEditorContent');
      if (updatedContent && updatedContent !== content) {
        setContent(updatedContent);
      }
      
      const updatedProjectMeta = localStorage.getItem('richTextEditorProjectMeta');
      if (updatedProjectMeta) {
        try {
          const parsedMeta = JSON.parse(updatedProjectMeta);
          const currentMetaStr = JSON.stringify(projectMeta);
          const newMetaStr = JSON.stringify(parsedMeta);
          if (newMetaStr !== currentMetaStr) {
            setProjectMeta(parsedMeta);
          }
        } catch (error) {
          console.error('Error parsing project metadata:', error);
        }
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []); // Empty dependency array to run only once

  // Gallery navigation functions
  const nextImage = () => {
    if (projectMeta?.galleryUrls) {
      setCurrentImageIndex((prev) => (prev + 1) % projectMeta.galleryUrls.length);
    }
  };

  const prevImage = () => {
    if (projectMeta?.galleryUrls) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? projectMeta.galleryUrls.length - 1 : prev - 1
      );
    }
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    
    const newOrientation = aspectRatio > 1.2 ? 'landscape' : aspectRatio < 0.8 ? 'portrait' : 'square';
    if (newOrientation !== imageOrientation) {
      setImageOrientation(newOrientation);
    }
  };

  const getGridCols = (count) => {
    if (imageOrientation === 'portrait') return 'grid-cols-1';
    if (count <= 2) return 'grid-cols-2';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  // Preload images for instant navigation
  useEffect(() => {
    if (!projectMeta?.galleryUrls) return;
    
    const preloadEssential = (url) => {
      if (!url || preloadedImages.has(url)) return;
      
      const img = new Image();
      img.src = url;
      img.onload = () => {
        setPreloadedImages(prev => {
          if (prev.has(url)) return prev;
          return new Set([...prev, url]);
        });
      };
    };

    const currentUrl = projectMeta.galleryUrls[currentImageIndex];
    const nextIdx = (currentImageIndex + 1) % projectMeta.galleryUrls.length;
    const prevIdx = (currentImageIndex - 1 + projectMeta.galleryUrls.length) % projectMeta.galleryUrls.length;
    
    preloadEssential(currentUrl);
    preloadEssential(projectMeta.galleryUrls[nextIdx]);
    preloadEssential(projectMeta.galleryUrls[prevIdx]);
  }, [currentImageIndex, projectMeta?.galleryUrls, preloadedImages.size]);

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!projectMeta?.galleryUrls || projectMeta.galleryUrls.length <= 1) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextImage();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projectMeta?.galleryUrls, currentImageIndex]);

  // Handle drag-to-scroll for thumbnails
  useEffect(() => {
    const container = thumbnailContainerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
      container.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      container.style.cursor = 'grab';
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, startX, scrollLeft]);

  return (
    <div className="min-h-screen bg-[#FFFAEB]">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Preview Label */}
            <div className="mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800">Preview Mode</h1>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  Live Preview
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                This preview updates automatically when you save in the editor
              </p>
            </div>

            {/* Project Details Section - Only show if project metadata exists */}
            {projectMeta && (
              <div className="mb-8 pb-8 border-b border-gray-200">
                <div className={`grid grid-cols-1 gap-12 lg:items-center ${
                  imageOrientation === 'portrait' 
                    ? 'lg:grid-cols-[400px_1fr]' 
                    : imageOrientation === 'landscape'
                    ? 'lg:grid-cols-[600px_1fr]'
                    : 'lg:grid-cols-2'
                }`}>
                  {/* Left - Gallery */}
                  {projectMeta.galleryUrls && projectMeta.galleryUrls.length > 0 && (
                    <div className="space-y-4 p-6 rounded-2xl lg:self-center" style={{
                      backgroundColor: 'rgba(30, 132, 121, 0.08)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid rgba(30, 132, 121, 0.2)'
                    }}>
                      <div className={`relative rounded-xl overflow-hidden bg-gray-200 shadow-lg transition-all duration-300 border border-gray-300 ${
                        imageOrientation === 'portrait' 
                          ? 'h-[500px]' 
                          : imageOrientation === 'landscape'
                          ? 'h-[350px]'
                          : 'h-80'
                      }`}>
                        <img
                          src={projectMeta.galleryUrls[currentImageIndex]}
                          alt={`${projectMeta.title || 'Project'} - Image ${currentImageIndex + 1}`}
                          className="w-full h-full object-contain transition-opacity duration-200"
                          onLoad={handleImageLoad}
                          style={{ opacity: preloadedImages.has(projectMeta.galleryUrls[currentImageIndex]) ? 1 : 0 }}
                        />
                        
                        {!preloadedImages.has(projectMeta.galleryUrls[currentImageIndex]) && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div>
                          </div>
                        )}
                        
                        {projectMeta.galleryUrls.length > 1 && (
                          <>
                            <button
                              onClick={prevImage}
                              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
                              style={{ color: '#105652' }}
                            >
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                              onClick={nextImage}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
                              style={{ color: '#105652' }}
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                            
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                              {projectMeta.galleryUrls.map((_, index) => (
                                <button
                                  key={index}
                                  onClick={() => setCurrentImageIndex(index)}
                                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    index === currentImageIndex
                                      ? 'bg-white w-8'
                                      : 'bg-white/50 hover:bg-white/75'
                                  }`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>

                      {projectMeta.galleryUrls.length > 1 && (
                        <div className="relative group">
                          <div 
                            ref={thumbnailContainerRef}
                            className="overflow-x-auto scrollbar-hide scroll-smooth"
                            style={{ cursor: 'grab' }}
                          >
                            <div className="flex gap-2 pb-2">
                              {projectMeta.galleryUrls.map((img, index) => (
                                <button
                                  key={index}
                                  onClick={(e) => {
                                    if (!isDragging) {
                                      setCurrentImageIndex(index);
                                    }
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className={`relative h-20 flex-shrink-0 rounded-lg overflow-hidden transition-all duration-300 ${
                                    index === currentImageIndex
                                      ? 'ring-4 ring-[#1E8479] scale-105'
                                      : 'opacity-60 hover:opacity-100'
                                  }`}
                                  style={{ width: 'calc(25% - 6px)', minWidth: '80px' }}
                                >
                                  <img
                                    src={img}
                                    alt={`Thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover pointer-events-none"
                                    draggable="false"
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Right - Project Info */}
                  <div className="flex flex-col p-6 rounded-2xl" style={{
                    backgroundColor: 'rgba(30, 132, 121, 0.08)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(30, 132, 121, 0.2)'
                  }}>
                    <div className="space-y-6 flex-1">
                      <div>
                        {projectMeta.title && (
                          <h1
                            className={`font-bold mb-3 ${
                              imageOrientation === 'portrait' ? 'text-3xl' : 'text-4xl'
                            }`}
                            style={{ color: '#105652' }}
                          >
                            {projectMeta.title}
                          </h1>
                        )}
                        {projectMeta.description && (
                          <p className={`text-gray-600 ${
                            imageOrientation === 'portrait' ? 'text-base' : 'text-lg'
                          }`}>{projectMeta.description}</p>
                        )}
                      </div>

                      {projectMeta.projectDetails && projectMeta.projectDetails.length > 0 && (
                        <div className={`grid gap-3 ${getGridCols(projectMeta.projectDetails.length)}`}>
                          {projectMeta.projectDetails.map((detail, index) => (
                            detail.value && (
                              <div
                                key={index}
                                className="relative overflow-hidden rounded-2xl p-3.5 transition-all duration-300 hover:scale-[1.02]"
                                style={{
                                  background: '#ffffff',
                                  boxShadow: '6px 6px 12px #d1d1d1, -6px -6px 12px #ffffff',
                                }}
                              >
                                <div className="relative z-10">
                                  <p
                                    className="text-xs font-bold uppercase tracking-wider mb-1.5"
                                    style={{ color: '#1E8479' }}
                                  >
                                    {detail.label}
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800 leading-snug">
                                    {detail.value}
                                  </p>
                                </div>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3 mt-6">
                      {projectMeta.projectLink && (
                        <a
                          href={projectMeta.projectLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-white transition-all duration-300 hover:scale-105 shadow-md"
                          style={{ backgroundColor: '#105652' }}
                        >
                          <span>View Project</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Content Preview */}
            <div 
              ref={contentRef}
              className="prose prose-lg max-w-none"
              style={{
                lineHeight: '1.8',
                fontSize: '16px',
                color: '#374151'
              }}
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(content || '<p class="text-gray-400">No content yet. Start typing in the editor...</p>', {
                  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'span', 'div', 'iframe', 'b', 'i', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'audio', 'video', 'source', 'font', 'button', 'svg', 'path', 'polyline', 'rect'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style', 'width', 'height', 'frameborder', 'allowfullscreen', 'controls', 'autoplay', 'loop', 'muted', 'poster', 'preload', 'type', 'data-*', 'id', 'face', 'size', 'color', 'title', 'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'd', 'points', 'x', 'y', 'rx', 'ry'],
                  ALLOW_DATA_ATTR: true,
                  ADD_TAGS: ['font', 'button', 'svg', 'path', 'polyline', 'rect'],
                  ADD_ATTR: ['style'],
                  KEEP_CONTENT: true
                })
              }}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      <style>{`
        .prose br {
          display: block;
          content: "";
          margin: 0.5em 0;
        }
        .prose p {
          margin-bottom: 1em;
        }
        
        /* Codeblock Syntax Highlighting */
        .token.comment { color: var(--syntax-comment, #6a737d); }
        .token.punctuation { color: var(--syntax-punctuation, #24292e); }
        .token.number { color: var(--syntax-number, #005cc5); }
        .token.string { color: var(--syntax-string, #032f62); }
        .token.operator { color: var(--syntax-operator, #d73a49); }
        .token.keyword { color: var(--syntax-keyword, #d73a49); }
        .token.function { color: var(--syntax-function, #6f42c1); }
        .token.variable { color: var(--syntax-variable, #e36209); }
        .token.tag { color: var(--syntax-tag, #22863a); }
        .token.attr-name { color: var(--syntax-attr-name, #6f42c1); }
        .token.builtin { color: var(--syntax-builtin, #005cc5); }
        .token.class-name { color: var(--syntax-class-name, #6f42c1); }
        .token.boolean { color: var(--syntax-number, #005cc5); }
        .token.selector { color: var(--syntax-tag, #22863a); }
        .token.property { color: var(--syntax-attr-name, #6f42c1); }
        .token.value { color: var(--syntax-string, #032f62); }
        .token.important { color: var(--syntax-keyword, #d73a49); font-weight: bold; }
        .token.attr-value { color: var(--syntax-string, #032f62); }
      `}</style>
    </div>
  );
}
